import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
	Box,
	Grid,
	Typography,
	Paper,
	Divider,
	CircularProgress,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { getService } from "../../reducers/service";
import { AMSModules, serviceNames } from "../../utils/constants";
import { getParticipantsByCourseNumber } from "../../reducers/TrainingParticipantReducer";
import {
	endOfLastWeek,
	endOfNextWeek,
	endOfThisWeek,
	getTotal,
	isBetween,
	last30DaysDate,
	next30DaysDate,
	startOfLastWeek,
	startOfNextWeek,
	startOfThisWeek,
	today,
} from "../../utils/functions";
import { CountTable } from "../../components/CountTable";
import { getAccessPermission } from "../../reducers/PermissionsReducer";
import { useLocation } from "react-router-dom/cjs/react-router-dom.min";

const PageMain = () => {
	const dispatch = useDispatch();
	const location = useLocation();
	const theme = useTheme();
	const isSmall = useMediaQuery(theme.breakpoints.down("md"));
	const userAgency = useSelector((state) => state.user.userAgency);
	const [loading, setLoading] = useState(true);
	const [applicantsByStatus, setApplicantsByStatus] = useState({});
	const [departmentCounts, setDepartmentCounts] = useState({});
	const [sectionCounts, setSectionCounts] = useState({});
	const [rankCounts, setRankCounts] = useState({});
	const [titleCounts, setTitleCounts] = useState({});
	const [participantList, setParticipantList] = useState([]);
	const [courseWeekCategoryCounts, setCourseWeekCategoryCounts] = useState({
		thisWeek: {},
		lastWeek: {},
		nextWeek: {},
	});
	const [participantWeekNameCounts, setParticipantWeekNameCounts] = useState({
		thisWeek: {},
		lastWeek: {},
		nextWeek: {},
	});
	const [certificationCounts, setCertificationCounts] = useState({
		next30Days: {},
		last30Days: {},
		current: {},
	});
	const [participantWeekCounts, setParticipantWeekCounts] = useState({
		thisWeek: {},
		lastWeek: {},
		nextWeek: {},
	});
	const isTrainingDashboard = location.pathname === "/training/dashboard";

	// Permissions
	const applicantsPermissions = getAccessPermission(
		"modules",
		"Applicants",
		userAgency,
	);
	const employeesPermissions = getAccessPermission(
		"modules",
		"Employees",
		userAgency,
	);
	const trainingPermissions = getAccessPermission(
		"modules",
		"Training",
		userAgency,
	);
	const coursesPermissions = getAccessPermission(
		"training",
		"Courses",
		userAgency,
	);
	const participantsPermissions = getAccessPermission(
		"training",
		"Participants",
		userAgency,
	);
	const certificationsPermissions = getAccessPermission(
		"training",
		"Certifications",
		userAgency,
	);

	const fetchApplicants = async () => {
		const service = getService(serviceNames.perApplicant);
		service.timeout = 20000;
		const res = await service.find({
			query: { $limit: -1, agencyId: userAgency, isDeleted: false, noThumbnails: true },
		});
		if (res) {
			const grouped = res.reduce((acc, applicant) => {
				const status =
					applicant["applicantStatusInfo.description"] || "N/A";
				acc[status] = (acc[status] || 0) + 1;
				return acc;
			}, {});
			setApplicantsByStatus(grouped);
		}
	};

	const fetchEmployees = async () => {
		const service = getService(serviceNames.personnel);
		service.timeout = 20000;
		const res = await service.find({
			query: { $limit: -1, agencyId: userAgency, isDeleted: false, noThumbnails: true },
		});
		if (res) {
			const deptGrouped = res.reduce((acc, emp) => {
				const dept = emp["departmentInfo.description"] || "N/A";
				acc[dept] = (acc[dept] || 0) + 1;
				return acc;
			}, {});
			const sectionGrouped = res.reduce((acc, emp) => {
				const section = emp["sectionInfo.description"] || "N/A";
				acc[section] = (acc[section] || 0) + 1;
				return acc;
			}, {});
			const rankGrouped = res.reduce((acc, emp) => {
				const rank = emp["rankInfo.description"] || "N/A";
				acc[rank] = (acc[rank] || 0) + 1;
				return acc;
			}, {});
			const titleGrouped = res.reduce((acc, emp) => {
				const title = emp["titlePositionInfo.description"] || "N/A";
				acc[title] = (acc[title] || 0) + 1;
				return acc;
			}, {});
			setDepartmentCounts(deptGrouped);
			setSectionCounts(sectionGrouped);
			setRankCounts(rankGrouped);
			setTitleCounts(titleGrouped);
		}
	};

	const fetchCourses = async () => {
		const service = getService(serviceNames.trainingCourse);
		service.timeout = 20000;
		const res = await service.find({
			query: { $limit: -1, agencyId: userAgency, isDeleted: false },
		});

		const part = await dispatch(
			getParticipantsByCourseNumber(res?.courseNumber),
		);

		if (res && part?.participants) {
			let weekCategoryCounts = {
				thisWeek: {},
				lastWeek: {},
				nextWeek: {},
			};

			res.forEach((course) => {
				if (!course.courseBeginDate) return;
				const begin = moment(course.courseBeginDate);
				const cat = course.category || "N/A";

				if (isBetween(begin, startOfThisWeek, endOfThisWeek)) {
					weekCategoryCounts.thisWeek[cat] =
						(weekCategoryCounts.thisWeek[cat] || 0) + 1;
				} else if (isBetween(begin, startOfLastWeek, endOfLastWeek)) {
					weekCategoryCounts.lastWeek[cat] =
						(weekCategoryCounts.lastWeek[cat] || 0) + 1;
				} else if (isBetween(begin, startOfNextWeek, endOfNextWeek)) {
					weekCategoryCounts.nextWeek[cat] =
						(weekCategoryCounts.nextWeek[cat] || 0) + 1;
				}
			});

			setCourseWeekCategoryCounts(weekCategoryCounts);

			const courseByName = new Map(res.map((c) => [c.courseName, c]));

			let weekParticipantCounts = {
				thisWeek: {},
				lastWeek: {},
				nextWeek: {},
			};

			part.participants.forEach((p) => {
				const courseObj = courseByName.get(p.course);
				if (!courseObj?.courseBeginDate) return;

				const begin = moment(courseObj.courseBeginDate);
				const courseName = p.course || "N/A";

				if (isBetween(begin, startOfThisWeek, endOfThisWeek)) {
					weekParticipantCounts.thisWeek[courseName] =
						(weekParticipantCounts.thisWeek[courseName] || 0) + 1;
				} else if (isBetween(begin, startOfLastWeek, endOfLastWeek)) {
					weekParticipantCounts.lastWeek[courseName] =
						(weekParticipantCounts.lastWeek[courseName] || 0) + 1;
				} else if (isBetween(begin, startOfNextWeek, endOfNextWeek)) {
					weekParticipantCounts.nextWeek[courseName] =
						(weekParticipantCounts.nextWeek[courseName] || 0) + 1;
				}
			});

			setParticipantWeekCounts(weekParticipantCounts);
		}
	};

	const fetchParticipant = async () => {
		const service = getService(serviceNames.perTraining);
		service.timeout = 20000;
		const res = await service.find({
			query: { $limit: -1, agencyId: userAgency, isDeleted: false },
		});
		if (res) {
			setParticipantList(res);
			let weekCategoryCounts = {
				thisWeek: {},
				lastWeek: {},
				nextWeek: {},
			};

			res.forEach((participant) => {
				if (!participant.attendedDate) return;

				const begin = moment(participant.attendedDate);
				const participantName = participant?.employeeDetails
					? `${participant.employeeDetails.firstName || ""} ${
							participant.employeeDetails.middleName || ""
						} ${participant.employeeDetails.lastName || ""}`.trim()
					: "N/A";

				const attendedHours = participant.hours || 0;

				if (isBetween(begin, startOfThisWeek, endOfThisWeek)) {
					weekCategoryCounts.thisWeek[participantName] =
						(weekCategoryCounts.thisWeek[participantName] || 0) +
						attendedHours;
				} else if (isBetween(begin, startOfLastWeek, endOfLastWeek)) {
					weekCategoryCounts.lastWeek[participantName] =
						(weekCategoryCounts.lastWeek[participantName] || 0) +
						attendedHours;
				} else if (isBetween(begin, startOfNextWeek, endOfNextWeek)) {
					weekCategoryCounts.nextWeek[participantName] =
						(weekCategoryCounts.nextWeek[participantName] || 0) +
						attendedHours;
				}
			});

			setParticipantWeekNameCounts(weekCategoryCounts);
		}
	};

	const fetchCertificate = async () => {
		const service = getService(serviceNames.perTrainingCertification);
		service.timeout = 20000;
		const res = await service.find({
			query: { $limit: -1, agencyId: userAgency, isDeleted: false },
		});

		if (res) {
			let weekCategoryCounts = {
				next30Days: {},
				last30Days: {},
				current: {},
			};

			res.forEach((cert) => {
				if (!cert.expireDate) return;
				const expire = moment(cert.expireDate);
				const certType = cert.certificationType || "N/A";

				if (expire >= today && expire <= next30DaysDate) {
					weekCategoryCounts.next30Days[certType] =
						(weekCategoryCounts.next30Days[certType] || 0) + 1;
				}
				if (expire < today && expire >= last30DaysDate) {
					weekCategoryCounts.last30Days[certType] =
						(weekCategoryCounts.last30Days[certType] || 0) + 1;
				}
				if (expire >= today) {
					weekCategoryCounts.current[certType] =
						(weekCategoryCounts.current[certType] || 0) + 1;
				}
			});

			setCertificationCounts(weekCategoryCounts);
		}
	};

	useEffect(() => {
		const loadAll = async () => {
			setLoading(true);
			try {
				if (isTrainingDashboard) {
					await Promise.all([
						fetchCourses(),
						fetchParticipant(),
						fetchCertificate(),
					]);
				} else {
					await Promise.all([
						fetchApplicants(),
						fetchEmployees(),
						fetchCourses(),
						fetchParticipant(),
						fetchCertificate(),
					]);
				}
			} catch (error) {
				console.error("Error while loading data:", error);
			} finally {
				setLoading(false);
			}
		};
		loadAll();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [userAgency, isTrainingDashboard]);

	const getApplicantsEmployeeGridSize = useCallback(
		(permission) => {
			const trainingPer =
				trainingPermissions?.Read &&
				(coursesPermissions?.Read ||
					participantsPermissions?.Read ||
					certificationsPermissions?.Read);
			if (!permission && !trainingPer) {
				return 12;
			} else if (!trainingPer && permission) {
				return 6;
			} else return 3;
		},
		[trainingPermissions, coursesPermissions, participantsPermissions, certificationsPermissions]
	);

	const getTrainingModulesGridSize = useCallback(
		(first, second) => {
			if (!first && !second) {
				return 12;
			} else if ((first && !second) || (!first && second)) {
				return 6;
			} else {
				return 4;
			}
		},
		[]
	);

	const memoizedCounts = useMemo(
		() => ({
			courseWeekThisWeek: Object.keys(courseWeekCategoryCounts.thisWeek || {}).length,
			courseWeekLastWeek: Object.keys(courseWeekCategoryCounts.lastWeek || {}).length,
			courseWeekNextWeek: Object.keys(courseWeekCategoryCounts.nextWeek || {}).length,
			participantWeekThisWeek: Object.keys(participantWeekNameCounts.thisWeek || {}).length,
			participantWeekLastWeek: Object.keys(participantWeekNameCounts.lastWeek || {}).length,
			participantWeekNextWeek: Object.keys(participantWeekNameCounts.nextWeek || {}).length,
			participantCountThisWeek: getTotal(participantWeekCounts.thisWeek),
			participantCountLastWeek: getTotal(participantWeekCounts.lastWeek),
			participantCountNextWeek: getTotal(participantWeekCounts.nextWeek),
		}),
		[courseWeekCategoryCounts, participantWeekNameCounts, participantWeekCounts]
	);

	if (loading) {
		return (
			<Box
				sx={{
					height: "100vh",
					width: "100%",
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					backgroundColor: "transparent",
				}}
			>
				<CircularProgress
					size={48}
					thickness={4}
					sx={{
						color: "white",
						mb: 2,
					}}
				/>
			</Box>
		);
	}

	return (
		<Box sx={{ p: 3, overflowY: "auto", maxHeight: "90vh", mt: 1 }}>
			<Typography
				variant="h2"
				sx={{
					textAlign: "center",
					fontWeight: "bold",
					mb: 4,
					color: theme.palette.colorFont,
				}}
			>
				{isTrainingDashboard ? "Training" : "Asset Management"} Home Page
			</Typography>

			<Grid container spacing={3}>
				{!isTrainingDashboard && applicantsPermissions?.Read && (
					<Grid
						item
						xs={12}
						md={getApplicantsEmployeeGridSize(
							employeesPermissions?.Read,
						)}
					>
						<Paper
							elevation={3}
							sx={{
								p: 2,
								height: "calc(100vh - 180px)",
								display: "flex",
								flexDirection: "column",
								overflowY: "auto",
							}}
						>
							<Typography
								variant="h5"
								sx={{ fontWeight: "bold" }}
							>
								Applicants
							</Typography>
							<Divider sx={{ my: 1, bgcolor: "black" }} />
							<Box sx={{ overflowY: "auto" }}>
								{
									<CountTable
										data={applicantsByStatus}
										label={"Status"}
										module={AMSModules.Applicants}
										showHours={null}
										autoHeight={true}
									/>
								}
							</Box>
						</Paper>
					</Grid>
				)}

				{!isTrainingDashboard && employeesPermissions?.Read && (
					<Grid
						item
						xs={12}
						md={getApplicantsEmployeeGridSize(
							applicantsPermissions?.Read,
						)}
					>
						<Paper
							elevation={3}
							sx={{
								p: 2,
								height: "calc(100vh - 180px)",
								display: "flex",
								flexDirection: "column",
								overflowY: "auto",
							}}
						>
							<Typography
								variant="h5"
								sx={{ fontWeight: "bold" }}
							>
								Employees
							</Typography>
							<Divider sx={{ my: 1, bgcolor: "black" }} />
							<Box sx={{ overflowY: "auto" }}>
								{
									<CountTable
										data={departmentCounts}
										label={"Department"}
										module={AMSModules.Employees}
										autoHeight
									/>
								}

								{
									<CountTable
										data={sectionCounts}
										label={"Section"}
										module={AMSModules.Employees}
										autoHeight
									/>
								}

								{
									<CountTable
										data={rankCounts}
										label={"Rank"}
										module={AMSModules.Employees}
										autoHeight
									/>
								}
								{
									<CountTable
										data={titleCounts}
										label={"Title"}
										module={AMSModules.Employees}
										autoHeight
									/>
								}
							</Box>
						</Paper>
					</Grid>
				)}

				{(isTrainingDashboard ||
					(trainingPermissions?.Read &&
						(coursesPermissions?.Read ||
							participantsPermissions?.Read ||
							certificationsPermissions?.Read))) && (
						<Grid
							item
							xs={12}
							md={
								isTrainingDashboard
									? 12
									: !applicantsPermissions?.Read ||
										!employeesPermissions?.Read
										? !applicantsPermissions?.Read &&
											!employeesPermissions?.Read
											? 12
											: 9
										: 6
							}
						>
							<Paper
								elevation={3}
								sx={{
									p: 2,
									height: "calc(100vh - 180px)",
									display: "flex",
									flexDirection: "column",
									overflowY: "auto",
								}}
							>
								<Typography
									variant="h5"
									sx={{ fontWeight: "bold" }}
								>
									Training
								</Typography>
								<Divider sx={{ my: 1, bgcolor: "black" }} />

								<Grid
									container
									sx={{ flexGrow: 1, height: "100%" }}
								>
									{coursesPermissions?.Read && (
										<Grid
											item
											xs={12}
											md={getTrainingModulesGridSize(
												participantsPermissions?.Read,
												certificationsPermissions?.Read,
											)}
											sx={{
												pr: 2,
												borderRight: "1px solid #ccc",
											}}
										>
											<Typography
												variant="subtitle2"
												sx={{
													fontWeight: "bold",
													fontSize: "17px",
												}}
											>
												Courses
											</Typography>
											<Divider
												sx={{ my: 1, bgcolor: "black" }}
											/>

											<Typography
												variant="body2"
												sx={{
													fontWeight: "bold",
													mt: 1,
												}}
											>
												This Week (
												{memoizedCounts.courseWeekThisWeek}
												)
											</Typography>
											{
												<CountTable
													data={
														courseWeekCategoryCounts.thisWeek
													}
													label={"Category"}
													module={AMSModules.Courses}
													autoHeight
												/>
											}

											<Typography
												variant="body2"
												sx={{
													fontWeight: "medium",
													mt: 1,
												}}
											>
												Participants By Course (
												{memoizedCounts.participantCountThisWeek}
												)
											</Typography>
											{
												<CountTable
													data={
														participantWeekCounts.thisWeek
													}
													label={"Course Name"}
													module={AMSModules.Courses}
													autoHeight
												/>
											}

											<Divider
												sx={{
													my: 1,
													mt: 2,
													bgcolor: "black",
												}}
											/>

											<Typography
												variant="body2"
												sx={{
													fontWeight: "bold",
													mt: 2,
												}}
											>
												Last Week (
												{memoizedCounts.courseWeekLastWeek}
												)
											</Typography>
											{
												<CountTable
													data={
														courseWeekCategoryCounts.lastWeek
													}
													label={"Category"}
													module={AMSModules.Courses}
													autoHeight
												/>
											}

											<Typography
												variant="body2"
												sx={{
													fontWeight: "medium",
													mt: 1,
												}}
											>
												Participants By Course (
												{memoizedCounts.participantCountLastWeek}
												)
											</Typography>
											{
												<CountTable
													data={
														participantWeekCounts.lastWeek
													}
													label={"Course Name"}
													module={AMSModules.Courses}
													autoHeight
												/>
											}

											<Divider
												sx={{
													my: 1,
													mt: 2,
													bgcolor: "black",
												}}
											/>

											<Typography
												variant="body2"
												sx={{
													fontWeight: "bold",
													mt: 2,
												}}
											>
												Next Week (
												{memoizedCounts.courseWeekNextWeek}
												)
											</Typography>
											{
												<CountTable
													data={
														courseWeekCategoryCounts.nextWeek
													}
													label={"Category"}
													module={AMSModules.Courses}
													autoHeight
												/>
											}

											<Typography
												variant="body2"
												sx={{
													fontWeight: "medium",
													mt: 1,
												}}
											>
												Participants By Course (
												{memoizedCounts.participantCountNextWeek}
												)
											</Typography>
											{
												<CountTable
													data={
														participantWeekCounts.nextWeek
													}
													label={"Course Name"}
													module={AMSModules.Courses}
													autoHeight
												/>
											}
										</Grid>
									)}

									{participantsPermissions?.Read && (
										<Grid
											item
											xs={12}
											md={getTrainingModulesGridSize(
												coursesPermissions?.Read,
												certificationsPermissions?.Read,
											)}
											sx={{
												px: 2,
												borderRight: "1px solid #ccc",
											}}
										>
											<Typography
												variant="subtitle2"
												sx={{
													fontWeight: "bold",
													mt: isSmall ? 2 : null,
													fontSize: "17px",
												}}
											>
												Participants
											</Typography>
											<Divider
												sx={{ my: 1, bgcolor: "black" }}
											/>
											<Typography
												variant="body2"
												sx={{
													fontWeight: "bold",
													mt: 1,
												}}
											>
												This Week (
												{memoizedCounts.participantWeekThisWeek}
												)
											</Typography>
											{
												<CountTable
													data={
														participantWeekNameCounts.thisWeek
													}
													participantList={
														participantList
													}
													label={"Name"}
													module={
														AMSModules.Participants
													}
													showHours={true}
													autoHeight
												/>
											}
											<Typography
												variant="body2"
												sx={{
													fontWeight: "bold",
													mt: 1,
												}}
											>
												Last Week (
												{memoizedCounts.participantWeekLastWeek}
												)
											</Typography>
											{
												<CountTable
													data={
														participantWeekNameCounts.lastWeek
													}
													participantList={
														participantList
													}
													label={"Name"}
													module={
														AMSModules.Participants
													}
													showHours={true}
													autoHeight
												/>
											}
											<Typography
												variant="body2"
												sx={{
													fontWeight: "bold",
													mt: 1,
												}}
											>
												Next Week (
												{memoizedCounts.participantWeekNextWeek}
												)
											</Typography>
											{
												<CountTable
													data={
														participantWeekNameCounts.nextWeek
													}
													participantList={
														participantList
													}
													label={"Name"}
													module={
														AMSModules.Participants
													}
													showHours={true}
													autoHeight
												/>
											}
										</Grid>
									)}

									{certificationsPermissions?.Read && (
										<Grid
											item
											xs={12}
											md={getTrainingModulesGridSize(
												coursesPermissions?.Read,
												participantsPermissions?.Read,
											)}
											sx={{ pl: 2 }}
										>
											<Typography
												variant="subtitle2"
												sx={{
													fontWeight: "bold",
													mt: isSmall ? 2 : null,
													fontSize: "17px",
												}}
											>
												Certifications
											</Typography>
											<Divider
												sx={{ my: 1, bgcolor: "black" }}
											/>
											<Typography
												variant="body2"
												sx={{
													fontWeight: "bold",
													mt: 1,
												}}
											>
												Expiring Next 30 Days
											</Typography>
											{
												<CountTable
													data={
														certificationCounts.next30Days
													}
													label={"Certificate Type"}
													module={
														AMSModules.Certifications
													}
													autoHeight
												/>
											}
											<Typography
												variant="body2"
												sx={{
													fontWeight: "bold",
													mt: 1,
												}}
											>
												Expired Last 30 Days
											</Typography>
											{
												<CountTable
													data={
														certificationCounts.last30Days
													}
													label={"Certificate Type"}
													module={
														AMSModules.Certifications
													}
													autoHeight
												/>
											}
											<Typography
												variant="body2"
												sx={{
													fontWeight: "bold",
													mt: 1,
												}}
											>
												Current
											</Typography>
											{
												<CountTable
													data={
														certificationCounts.current
													}
													label={"Certificate Type"}
													module={
														AMSModules.Certifications
													}
													autoHeight
												/>
											}
										</Grid>
									)}
								</Grid>
							</Paper>
						</Grid>
					)}
			</Grid>
		</Box>
	);
};

export default PageMain;
